import React, { memo, useCallback, useEffect, useState, useMemo } from "react";
import { useNavigationState } from "@react-navigation/native";
import { StackNavigationOptions, createStackNavigator } from "@react-navigation/stack";
import { CardStyleInterpolators, HeaderStyleInterpolators } from "@react-navigation/stack";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import * as Device from "expo-device";
import CheckClientInfo from "../screens/auth/CheckClientInfo";
import WorkerHomeTab from "./worker/Home";
import ClientHomeTab from "./client/Home";
import CreateMissionStack from "./CreateMissionStack";
import { useI18n } from "../hooks/i18n-hooks";
import WelcomeScreen from "../screens/auth/Welcome";
import TimesheetWorkerInput from "../screens/user/TimesheetWorkerInput";
import TimesheetClientReviewScreen from "../screens/client/TimesheetClientReview";
import TimesheetWorkerReviewScreen from "../screens/user/TimesheetWorkerReview";
import PdfViewerScreen from "../screens/PdfViewerScreen";
import { ClientStatus, KycStatus, ProfileType } from "../__generated__/graphql";
import SalaryCountingDetailsScreen from "../screens/SalaryCountingDetails";
import WorkerProfileEditorScreen from "../screens/worker/WorkerProfileEditor";
import ClientInvitationDetailsScreen from "../screens/client/InvitationDetails";
import SendInvitationScreen from "../screens/client/SendInvitationScreen";
import ConfigureLppScreen from "../screens/user/ConfigureLpp";
import MissionTerminationScreen from "../screens/user/MissionTermination";
import ContractSigningScreen from "../screens/user/ContractSigningScreen";
import { RootStackParamList } from "./RootNavigation";
import WorkerInvitationDetailsScreen from "../screens/user/WorkerInvitationDetails";
import WorkerInvitationDetailsForClientScreen from "../screens/user/WorkerInvitationDetailsForClient";
import MissionTimeSheetScreen from "../screens/user/MissionTimeSheetScreen";
import ResendEmailVerification from "../screens/user/ResendEmailVerificationScreen";
import { Platform } from "react-native";
import Icon, { IconName } from "../components/Icon";
import BankAccountEditorScreen from "../screens/profile-editor/BankAccountEditor";
import ClientProfileEditorScreen from "../screens/client/ClientProfileEditor";
import AccountEditorScreen from "../screens/user/AccountEditor";
import PrivateClientEditorScreen from "../screens/user/PrivateClientEditor";
import ClientEditorScreen from "../screens/user/ClientEditor";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import NotificationsModal from "../components/NotificationsModal";
import ClientListScreen from "../screens/worker/ClientListScreen";
import ClientDetailsScreen from "../screens/worker/ClientDetailsScreen";
import SalaryDetailsScreen from "../screens/worker/SalaryDetails";
import FeesDetailsScreen from "../screens/FeesDetails";
import InvoiceDetailsScreen from "../screens/client/InvoiceDetails";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import { useModalCardStyleInterpolator } from "../hooks/use-modal-card-style-interpolator";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import PreIdVerification from "../screens/auth/PreIdVerification";
import IdVerificationPending from "../screens/auth/IdVerificationPending";
import IdVerificationRetry from "../screens/auth/IdVerificationRetry";
import VeriffScreen from "../screens/user/VeriffScreen";
import SkribbleSampleScreen from "../screens/user/SkribbleSampleScreen";
import CctDetailsScreen from "../screens/user/CctDetails";
import { useRecoilState } from "recoil";
import { notificationEnabledState } from "../atoms/push-notifications";
import PostOnboardingProfileEditorScreen from "../screens/profile-editor/PostOnboardingProfileEditor";
import CivilStateEditorScreen from "../screens/profile-editor/CivilStateEditor";
import ChildrenEditorScreen from "../screens/profile-editor/ChildrenEditor";
import { ModalScreenWrapper } from "./ModalScreenWrapper";
import FiscalAttestationEditorScreen from "../screens/profile-editor/FiscalAttestationEditor";
import { usePostOnboardingData } from "../hooks/use-postonboarding-data";
import SignupFinishedScreen from "../screens/auth/SignupFinished";
import PostOnboardingPrepareScreen from "../screens/auth/PostOnboardingPrepare";
import PayrollBreakdownListScreen from "../screens/user/PayrollBreakdownListScreen";
import PayrollBreakdownDetailsScreen from "../screens/user/PayrollBreakdownDetailsScreen";
import PayrollBreakdownEditorScreen from "../screens/client/PayrollBreakdownEditorScreen";
import PayrollBreakdownTimeSheetScreen from "../screens/user/PayrollBreakdownTimeSheetScreen";
import DiplomaAndTrainingEditorScreen from "../screens/profile-editor/DiplomaAndTrainingEditor";
import PrivateEmailEditorScreen from "../screens/profile-editor/PrivateEmailEditor";
import HomeAddressEditorScreen from "../screens/profile-editor/HomeAddressEditor";
import PhoneNumberEditorScreen from "../screens/user/PhoneNumberEditor";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../hooks/use-current-profile";
import ClientVerificationPending from "../screens/auth/ClientVerificationPending";
import CheckPrivateClientInfo from "../screens/auth/CheckPrivateClientInfo";
import InviteClientInfoScreen from "../screens/worker/InviteClientInfo";
import PresentationCarouselScreen from "../screens/auth/PresentationCarousel/PresentationCarousel";
import SignupSigninStack from "./SignupSigninStack";
import VerificationFailed from "../screens/auth/VerificationFailed";
import CheckWorkerInfo from "../screens/auth/CheckWorkerInfo";
import IJMEditorNavScreen from "../screens/client/IJMEditorNavScreen";
import ContractPreviewScreen from "../screens/user/ContractPreviewScreen";
import MissionSelectSignerScreen from "../screens/user/MissionSelectSignerScreen";
import IJMClientViewer from "../components/editors/ijm/screens/IJMClientViewer";

const Stack = createStackNavigator<RootStackParamList>();

const headerBackImage = (icon: IconName = "back") => {
    return (
        <Icon
            name={icon}
            size={28}
            style={{
                marginLeft: Platform.OS === "ios" ? 10 : 0,
                marginRight: 5,
            }}
        />
    );
};

const useRouteName = () =>
    (useNavigationState((state) => {
        if (!state) {
            return undefined;
        }
        return state.routes[state.routes.length - 1].name;
    }) || "Welcome") as keyof RootStackParamList;

const Navigator = () => {
    const theme = useTheme();
    const i18n = useI18n();
    const [isNotificationsPermissionsModalOpen, setIsNotificationsPermissionsModalOpen] =
        useState(false);

    const isLarge = useIsScreenSizeMin("large");
    const cardStyleInterpolator = useCardStyleInterpolator();
    const modalCardStyleInterpolator = useModalCardStyleInterpolator();

    const { status, user } = React.useContext(AuthManagerContext);
    const { profileType } = React.useContext(ProfileSwitcherManagerContext);
    const [notificationEnabled, setNotificationEnabled] = useRecoilState(notificationEnabledState);

    const routeName = useRouteName();

    useEffect(() => {
        if (
            Device.isDevice &&
            Platform.OS !== "web" &&
            notificationEnabled === undefined &&
            (routeName === "IdVerificationPending" || routeName === "Home")
        ) {
            setIsNotificationsPermissionsModalOpen(true);
        }
    }, [notificationEnabled, routeName]);

    const onNotificationsFinished = useCallback(
        (result: boolean) => {
            setIsNotificationsPermissionsModalOpen(false);
            setNotificationEnabled(result);
        },
        [setNotificationEnabled],
    );

    const getModalScreenConfig = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (Component: React.FC<any>, title: string, fitContent = false) => {
            return {
                // eslint-disable-next-line react/display-name
                component: memo((props) => (
                    <ModalScreenWrapper
                        component={Component}
                        title={title}
                        fitContent={fitContent}
                        {...props}
                    />
                )),
                options: {
                    title,
                    headerShown: true,
                    headerBackButtonDisplayMode: "minimal",
                    headerBackImage: () => headerBackImage("close"),
                    gestureEnabled: false,
                    cardStyleInterpolator: modalCardStyleInterpolator,
                    headerStyleInterpolator: HeaderStyleInterpolators.forSlideUp,
                    ...(isLarge
                        ? {
                              detachPreviousScreen: false,
                              headerShown: false,
                              animation: "default",
                              cardStyle: {
                                  backgroundColor: "transparent",
                              },
                          }
                        : {}),
                } as StackNavigationOptions,
            };
        },
        [isLarge, modalCardStyleInterpolator],
    );

    const modalScreensProps = useMemo(
        () => ({
            SendInvitation: getModalScreenConfig(
                SendInvitationScreen,
                i18n.t("invitation.send.title"),
            ),
            InviteClientInfo: getModalScreenConfig(
                InviteClientInfoScreen,
                i18n.t("screens.invite_client.title"),
                true,
            ),
            CivilStateEdit: getModalScreenConfig(
                CivilStateEditorScreen,
                i18n.t("screens.profile_editor.civil_state.title"),
            ),
            FiscalAttestationEdit: getModalScreenConfig(
                FiscalAttestationEditorScreen,
                i18n.t("screens.profile_editor.fiscal_attestation.title"),
            ),
            PrivateEmailEdit: getModalScreenConfig(
                PrivateEmailEditorScreen,
                i18n.t("screens.profile_editor.private_email.title"),
            ),
            HomeAddressEdit: getModalScreenConfig(
                HomeAddressEditorScreen,
                i18n.t("screens.profile_editor.home_address.title"),
            ),
            ChildrenEdit: getModalScreenConfig(
                ChildrenEditorScreen,
                i18n.t("screens.profile_editor.children.title"),
            ),
            BankAccountEdit: getModalScreenConfig(
                BankAccountEditorScreen,
                i18n.t("screens.profile_editor.bank_account.title"),
            ),
            FeesDetails: getModalScreenConfig(
                FeesDetailsScreen,
                i18n.t("screens.fees_and_deductions.title"),
            ),
        }),
        [getModalScreenConfig, i18n],
    );

    const { isValid: isPostOnboardingValid } = usePostOnboardingData();

    const currentProfile = useCurrentProfile();

    return (
        <>
            <NotificationsModal
                id="notifications-modal"
                visible={isNotificationsPermissionsModalOpen}
                animationType="slide"
                statusBarTranslucent
                onFinished={onNotificationsFinished}
            />
            <Stack.Navigator
                screenOptions={{
                    headerStyle: {
                        backgroundColor: theme.colors.background,
                    },
                    headerMode: "screen",
                    headerStatusBarHeight: 0,
                    headerBackButtonDisplayMode: "minimal",
                    headerBackImage: () => headerBackImage(),
                    headerBackTestID: "button-back",
                    headerTitleAlign: "center",
                    headerTitleStyle: {
                        ...theme.fonts.titleMedium,
                    },
                    gestureEnabled: true,
                    headerShadowVisible: false,
                    cardStyleInterpolator,
                    headerStyleInterpolator: HeaderStyleInterpolators.forUIKit,
                    ...(Platform.OS === "web"
                        ? {
                              cardStyle: {
                                  height: "100%",
                              },
                          }
                        : {}),
                }}
            >
                {status === AuthStatus.Out && (
                    <Stack.Group>
                        <Stack.Screen
                            name="Welcome"
                            component={WelcomeScreen}
                            options={{
                                headerShown: false,
                                cardStyleInterpolator: CardStyleInterpolators.forNoAnimation,
                            }}
                        />
                        <Stack.Screen
                            name="PresentationCarousel"
                            component={PresentationCarouselScreen}
                            options={{ headerShown: false }}
                        />
                        <Stack.Screen
                            name="SignupSigninStack"
                            component={SignupSigninStack}
                            options={{
                                headerShown: false,
                                gestureEnabled: false,
                                cardStyleInterpolator:
                                    CardStyleInterpolators.forRevealFromBottomAndroid,
                            }}
                        />
                        <Stack.Screen
                            name="NotFoundScreen"
                            component={NotFoundScreen}
                            options={{ headerShown: false }}
                        />
                        <Stack.Screen
                            name="CheckWorkerInfo"
                            component={CheckWorkerInfo}
                            options={{ title: i18n.t("screens.auth.check_worker_info.title") }}
                        />
                    </Stack.Group>
                )}
                {status === AuthStatus.In &&
                    user &&
                    (user?.kycStatus?.status === KycStatus.Rejected ? (
                        <Stack.Screen
                            name="IdVerificationRetry"
                            component={IdVerificationRetry}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ) : user?.kycStatus?.status === KycStatus.Init ? (
                        <>
                            <Stack.Screen
                                name="PreIdVerification"
                                component={PreIdVerification}
                                options={{
                                    headerShown: false,
                                }}
                            />
                            <Stack.Screen
                                name="VeriffScreen"
                                component={VeriffScreen}
                                options={{
                                    headerShown: false,
                                }}
                            />
                        </>
                    ) : [KycStatus.Pending, KycStatus.Submitted].includes(
                          user?.kycStatus?.status || KycStatus.Init,
                      ) ? (
                        <Stack.Screen
                            // Pending and Submitted KycStatus are handled here
                            name="IdVerificationPending"
                            component={IdVerificationPending}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ) : user.kycStatus?.status === KycStatus.Banned ||
                      currentProfile?.client?.status === ClientStatus.Rejected ? (
                        <Stack.Screen
                            // ID verification banned OR client business is rejected
                            name="VerificationFailed"
                            component={VerificationFailed}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ) : profileType === ProfileType.Client &&
                      currentProfile?.client?.status === ClientStatus.Pending ? (
                        <Stack.Screen
                            // Waiting for manual client verification
                            name="ClientVerificationPending"
                            component={ClientVerificationPending}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ) : profileType === ProfileType.Worker && !isPostOnboardingValid ? (
                        <Stack.Screen
                            name="PostOnboardingPrepare"
                            component={PostOnboardingPrepareScreen}
                            options={{
                                headerShown: false,
                            }}
                        />
                    ) : (
                        user?.kycStatus?.status === KycStatus.Accepted && (
                            <Stack.Group
                                screenOptions={{
                                    headerShown: false,
                                    cardStyleInterpolator: cardStyleInterpolator,
                                }}
                            >
                                {profileType === ProfileType.Worker && (
                                    <Stack.Group>
                                        <Stack.Screen
                                            name="Home"
                                            component={WorkerHomeTab}
                                            options={{
                                                title: i18n.t("home"),
                                                cardStyleInterpolator:
                                                    CardStyleInterpolators.forNoAnimation,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="WorkerInvitationDetails"
                                            component={WorkerInvitationDetailsScreen}
                                            options={{
                                                headerShown: true,
                                                headerBackButtonDisplayMode: "minimal",
                                                headerBackImage: () => headerBackImage("close"),
                                                title: "",
                                                gestureEnabled: false,
                                                cardStyleInterpolator:
                                                    CardStyleInterpolators.forVerticalIOS,
                                                headerStyleInterpolator:
                                                    HeaderStyleInterpolators.forSlideUp,
                                            }}
                                        />
                                    </Stack.Group>
                                )}
                                {profileType === ProfileType.Client && (
                                    <Stack.Group>
                                        <Stack.Screen
                                            name="Home"
                                            component={ClientHomeTab}
                                            options={{
                                                title: i18n.t("home"),
                                                cardStyleInterpolator:
                                                    CardStyleInterpolators.forNoAnimation,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ClientInvitationDetails"
                                            component={ClientInvitationDetailsScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t(`invitation.details.title`),
                                                gestureEnabled: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="WorkerInvitationDetails"
                                            component={WorkerInvitationDetailsForClientScreen}
                                            options={{
                                                headerShown: true,
                                                headerBackButtonDisplayMode: "minimal",
                                                headerBackImage: () => headerBackImage("close"),
                                                title: "",
                                                gestureEnabled: false,
                                                cardStyleInterpolator:
                                                    CardStyleInterpolators.forVerticalIOS,
                                                headerStyleInterpolator:
                                                    HeaderStyleInterpolators.forSlideUp,
                                            }}
                                        />
                                    </Stack.Group>
                                )}
                                <Stack.Screen
                                    name="SignupFinished"
                                    component={SignupFinishedScreen}
                                    options={{
                                        headerShown: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="SkribbleSampleScreen"
                                    component={SkribbleSampleScreen}
                                    options={{
                                        headerShown: true,
                                        title: "Sample skribble",
                                    }}
                                />
                                <Stack.Screen
                                    name="ResendEmailVerification"
                                    component={ResendEmailVerification}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t(
                                            "screens.user.resend_verification_email.title",
                                        ),
                                    }}
                                />
                                <Stack.Screen
                                    name="WorkerProfileEdit"
                                    component={WorkerProfileEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.worker_profile_edit.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="PhoneNumberEdit"
                                    component={PhoneNumberEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.phone_number_edit.title"),
                                    }}
                                />

                                <Stack.Screen
                                    name="ClientProfileEdit"
                                    component={ClientProfileEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.client_profile_edit.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="IJMClientViewer"
                                    component={IJMClientViewer}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("ijm.screen_title"),
                                    }}
                                />

                                <Stack.Screen
                                    name="ClientEdit"
                                    component={ClientEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.client_edit.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="AccountEdit"
                                    component={AccountEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.account_edit.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="PrivateClientEdit"
                                    component={PrivateClientEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.private_client_edit.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="BankAccountEdit"
                                    component={BankAccountEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.profile_editor.bank_account.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="DiplomaAndTrainingEdit"
                                    component={DiplomaAndTrainingEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t(
                                            "screens.profile_editor.diploma_and_training.title",
                                        ),
                                    }}
                                />
                                <Stack.Screen
                                    name="ConfigureLpp"
                                    component={ConfigureLppScreen}
                                    options={{
                                        title: i18n.t("screens.configure_lpp.title"),
                                        headerShown: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="CctDetails"
                                    component={CctDetailsScreen}
                                    options={{
                                        headerShown: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="ContractSigningScreen"
                                    component={ContractSigningScreen}
                                    options={{
                                        headerShown: true,
                                        headerBackTitle: i18n.t("cancel"),
                                        headerBackButtonDisplayMode: "minimal",
                                        title: "",
                                        gestureEnabled: false,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                        headerStyleInterpolator:
                                            HeaderStyleInterpolators.forSlideUp,
                                    }}
                                />
                                <Stack.Screen
                                    name="ContractPreviewScreen"
                                    component={ContractPreviewScreen}
                                    options={{
                                        headerShown: true,
                                        headerBackTitle: i18n.t("cancel"),
                                        headerBackButtonDisplayMode: "minimal",
                                        title: "",
                                        gestureEnabled: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="MissionSelectSignerScreen"
                                    component={MissionSelectSignerScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.mission_select_signer.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="CreateMissionStack"
                                    component={CreateMissionStack}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: false,
                                        ...(isLarge
                                            ? {
                                                  detachPreviousScreen: false,
                                                  animation: "default",
                                                  cardStyle: {
                                                      backgroundColor: "transparent",
                                                  },
                                              }
                                            : {}),
                                        cardStyleInterpolator: modalCardStyleInterpolator,
                                        headerStyleInterpolator:
                                            HeaderStyleInterpolators.forSlideUp,
                                    }}
                                />
                                <Stack.Screen
                                    name="MissionTimeSheets"
                                    component={MissionTimeSheetScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("timesheet_list.title"),
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="MissionTermination"
                                    component={MissionTerminationScreen}
                                    options={{
                                        headerShown: true,
                                        headerBackImage: () => headerBackImage("close"),
                                        title: i18n.t("contract.cancel_contract"),
                                        gestureEnabled: false,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                        headerStyleInterpolator:
                                            HeaderStyleInterpolators.forSlideUp,
                                    }}
                                />
                                <Stack.Screen
                                    name="TimesheetWorkerInput"
                                    component={TimesheetWorkerInput}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: false,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                    }}
                                />
                                <Stack.Screen
                                    name="TimesheetClientReview"
                                    component={TimesheetClientReviewScreen}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="TimesheetWorkerReview"
                                    component={TimesheetWorkerReviewScreen}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="SalaryCountingDetails"
                                    component={SalaryCountingDetailsScreen}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: true,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                    }}
                                />
                                <Stack.Screen
                                    name="PdfViewer"
                                    component={PdfViewerScreen}
                                    options={{
                                        headerShown: true,
                                        gestureEnabled: false,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                        headerBackImage: () => (
                                            <Icon
                                                name="close"
                                                size={28}
                                                style={{
                                                    marginLeft: Platform.OS === "ios" ? 10 : 0,
                                                    marginRight: 5,
                                                }}
                                            />
                                        ),
                                    }}
                                />
                                <Stack.Screen
                                    name="SendInvitation"
                                    {...modalScreensProps.SendInvitation}
                                />
                                <Stack.Screen
                                    name="InviteClientInfo"
                                    {...modalScreensProps.InviteClientInfo}
                                />
                                <Stack.Screen
                                    name="ClientList"
                                    component={ClientListScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.client_list.title"),
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="ClientDetails"
                                    component={ClientDetailsScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.client_details.title"),
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="SalaryDetails"
                                    component={SalaryDetailsScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.user.salaries.salary_sheet"),
                                        gestureEnabled: true,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                        headerBackImage: () => (
                                            <Icon
                                                name="close"
                                                size={28}
                                                style={{
                                                    marginLeft: Platform.OS === "ios" ? 10 : 0,
                                                    marginRight: 5,
                                                }}
                                            />
                                        ),
                                        animation: "slide_from_bottom",
                                    }}
                                />
                                <Stack.Screen
                                    name="FeesDetails"
                                    {...modalScreensProps.FeesDetails}
                                />
                                <Stack.Screen
                                    name="InvoiceDetails"
                                    component={InvoiceDetailsScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.user.invoices.invoice"),
                                        gestureEnabled: true,
                                        cardStyleInterpolator:
                                            CardStyleInterpolators.forVerticalIOS,
                                        headerBackImage: () => (
                                            <Icon
                                                name="close"
                                                size={28}
                                                style={{
                                                    marginLeft: Platform.OS === "ios" ? 10 : 0,
                                                    marginRight: 5,
                                                }}
                                            />
                                        ),
                                    }}
                                />
                                <Stack.Screen
                                    name="PayrollBreakdownList"
                                    component={PayrollBreakdownListScreen}
                                    options={{
                                        headerShown: false,
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="PayrollBreakdownDetails"
                                    component={PayrollBreakdownDetailsScreen}
                                    options={{
                                        headerShown: false,
                                        title: i18n.t("screens.payroll_breakdown.title"),
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="PayrollBreakdownEditor"
                                    component={PayrollBreakdownEditorScreen}
                                    options={{
                                        headerShown: false,
                                        title: i18n.t("screens.payroll_breakdown_editor.title"),
                                        gestureEnabled: true,
                                    }}
                                />
                                <Stack.Screen
                                    name="PayrollBreakdownTimeSheetScreen"
                                    component={PayrollBreakdownTimeSheetScreen}
                                    options={{
                                        headerShown: false,
                                        title: i18n.t("screens.payroll_breakdown_timesheets.title"),
                                        gestureEnabled: true,
                                    }}
                                />

                                {/* IJMEditorNavScreen is used through use-ijm-editor.ts hook */}
                                <Stack.Screen
                                    name="IJMEditorNavScreen"
                                    component={IJMEditorNavScreen}
                                    options={{
                                        headerShown: false,
                                        title: i18n.t("ijm.screen_title"),
                                        gestureEnabled: false,
                                        animation: "slide_from_bottom",
                                        cardStyleInterpolator: modalCardStyleInterpolator,
                                        ...(isLarge
                                            ? {
                                                  detachPreviousScreen: false,
                                                  headerShown: false,
                                                  animation: "default",
                                                  cardStyle: {
                                                      backgroundColor: "transparent",
                                                  },
                                              }
                                            : {}),
                                    }}
                                />
                                <Stack.Screen
                                    name="NotFoundScreen"
                                    component={NotFoundScreen}
                                    options={{ headerShown: false }}
                                />
                            </Stack.Group>
                        )
                    ))}
                <Stack.Screen
                    name="CheckClientInfo"
                    component={CheckClientInfo}
                    options={{
                        title: i18n.t("screens.auth.check_client_info.title"),
                        gestureEnabled: false,
                    }}
                />
                <Stack.Screen
                    name="CheckPrivateClientInfo"
                    component={CheckPrivateClientInfo}
                    options={{
                        title: i18n.t("screens.auth.check_private_client_info.title"),
                        gestureEnabled: false,
                    }}
                />
                {profileType && (
                    <Stack.Group>
                        <Stack.Screen
                            name="PostOnboardingProfileEditor"
                            component={PostOnboardingProfileEditorScreen}
                            options={{
                                headerShown: true,
                                title: i18n.t("screens.profile_editor.title"),
                            }}
                        />
                        <Stack.Screen name="CivilStateEdit" {...modalScreensProps.CivilStateEdit} />
                        <Stack.Screen
                            name="FiscalAttestationEdit"
                            {...modalScreensProps.FiscalAttestationEdit}
                        />
                        <Stack.Screen
                            name="PrivateEmailEdit"
                            {...modalScreensProps.PrivateEmailEdit}
                        />
                        <Stack.Screen
                            name="HomeAddressEdit"
                            {...modalScreensProps.HomeAddressEdit}
                        />
                        <Stack.Screen name="ChildrenEdit" {...modalScreensProps.ChildrenEdit} />
                        <Stack.Screen
                            name="BankAccountEditModal"
                            {...modalScreensProps.BankAccountEdit}
                        />
                    </Stack.Group>
                )}
            </Stack.Navigator>
        </>
    );
};

export default Navigator;
